/**
 * Authentication Context Provider
 * Uses short-lived access tokens (localStorage) + refresh tokens (HttpOnly cookie).
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';
import api from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  // Login function
  const login = useCallback(async (identifier, password) => {
    const response = await authAPI.login(identifier, password);

    localStorage.setItem('token', response.access_token);
    localStorage.setItem('user', JSON.stringify(response.user));

    setToken(response.access_token);
    setUser(response.user);

    return response.user;
  }, []);

  // Register function
  const register = useCallback(async (data) => {
    const response = await authAPI.register(data);

    localStorage.setItem('token', response.access_token);
    localStorage.setItem('user', JSON.stringify(response.user));

    setToken(response.access_token);
    setUser(response.user);

    return response.user;
  }, []);

  // R29 — guest-signup helper: inject a session directly (bypass login endpoint)
  const setSession = useCallback((accessToken, userObj) => {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userObj));
    setToken(accessToken);
    setUser(userObj);
  }, []);

  // Logout function - also revokes refresh token on server
  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (_) {
      // best-effort
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }, []);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      const updatedUser = response.data;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  }, []);

  // Check if user has specific role (case-insensitive)
  const hasRole = useCallback((role) => {
    if (!user || !user.roles) return false;
    const normalizedRole = role.toUpperCase();
    return user.roles.some(r => r.toUpperCase() === normalizedRole);
  }, [user]);

  // Check if user has any of the specified roles (case-insensitive)
  const hasAnyRole = useCallback((roles) => {
    if (!user || !user.roles) return false;
    const normalizedUserRoles = user.roles.map(r => r.toUpperCase());
    return roles.some(role => normalizedUserRoles.includes(role.toUpperCase()));
  }, [user]);

  const isAdmin = useCallback(() => {
    return hasAnyRole(['ADMIN', 'CEO']);
  }, [hasAnyRole]);

  const isStaff = useCallback(() => {
    return hasRole('STAFF');
  }, [hasRole]);

  const mustChangePassword = useCallback(() => {
    return user?.must_change_password === true;
  }, [user]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    register,
    setSession,
    logout,
    refreshUser,
    hasRole,
    hasAnyRole,
    isAdmin,
    isStaff,
    mustChangePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
